<template>
  <div class="i-frequently-asked-questions">
    <div
      v-for="(set, index) in FAQS.filter((e) => e.visible)"
      :key="index"
      class="set"
    >
      <iBaseHeader
        highlight-text=""
        :text="set.question"
        align="start"
      />
      <iBaseExpandableParagraph
        class="answer"
        :text="set.answer"
        text-size="unset"
        :collapsed-length="set.answer.length < 150 ? 150 : descriptionMaxlength"
        :cta-color="azureRadiance500"
        :show-icon="false"
      />
    </div>

    <div class="card">
      <iBaseHeader
        highlight-text=""
        text="Still Need Help?"
        align="center"
      />
      <div>Can’t find the answer you’re looking for? Please chat to our friendly support team.</div>
      <iBaseButton
        v-bind="MAIN_BUTTON_STYLE"
        text="Get in Touch"
        :icon-url="iconFaceSmileWhite"
        icon-width="22px"
        @clicked="mailSupport"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { useUserStore } from "@/stores/user"
import { azureRadiance500, bluePurpleGradient, white } from "@/styles/colors"
import { breakpointLg, breakpointSm, breakpointXl } from "@/styles/variables"

import useIsScreenSize from "@/composables/useIsScreenSize"

import iBaseHeader from "@/components/base/BaseHeader.vue"
import iBaseButton from "@/components/base/BaseButton.vue"
import iBaseExpandableParagraph from "@/components/base/BaseExpandableParagraph.vue"

// ASSETS
import iconFaceSmileWhite from "@/assets/icons/icon-face-smile-white.svg"

// COMPOSABLES AND STORES
const isMobileOrLarger = useIsScreenSize(breakpointSm)
const isDesktopOrLarger = useIsScreenSize(breakpointLg)
const isXlOrLarger = useIsScreenSize(breakpointXl)
const userStore = useUserStore()

// CONSTANTS
const MAIN_BUTTON_STYLE = {
  backgroundImage: bluePurpleGradient,
  borderRadius: "8px",
  fontWeight: "500",
  textColor: white,
  width: "fit-content",
  padding: "10px 16px",
  size: "small"
}
const FAQS = [
  {
    question: "Why should I get a subscription?",
    answer:
      "Our coaches specialize in helping you improve your interview skills, feedback, and strategies to effectively present your strengths and experiences. Also, whether you are looking to advance in your current field or explore new career paths, our coaches offer personalized guidance to help you achieve your career goals faster. This includes career assessments, goal setting, resume reviews, and action planning.We have several packages available that you can explore to find the one that best suits your needs and preferences.",
    visible: userStore.isCoachingOnce || userStore.isCoachingCancelled
  },
  {
    question: "Can I book a single session with my coach?",
    answer: `Yes! You can book a 30-min on-demand session with your coach without needing to subscribe. To do so, look for and click on the "Buy an Extra Session" button next to your coach's picture or when you hover on the ••• button. Select your desired time and date from your coach's calendar and pay when redirected to the checkout page.`,
    visible: true
  },
  {
    question: "What to do if my coach doesn't have any availability?",
    answer:
      "We understand how important it is to find a coach who fits your schedule. If your coach has no availability, you can <a href='https://survey.zigpoll.com/2tVLrZ1upJBnDCLQ3/2wUgT342Yyt8GCi7d' target='_blank'>join their waitlist</a>. We’ll notify you as soon as a spot opens up that fits your schedule. Or if you would like personal assistance, our team is here to help. Please fill out <a href='https://survey.zigpoll.com/2tVLrZ1upJBnDCLQ3/2wUhqYcrMubg4Wmxw' target='_blank'>this form</a>, and a specialist will reach out to work with you personally to secure a session time that fits your schedule.",
    visible: true
  },
  {
    question: "How do I gift someone or pay for someone's coaching session?",
    answer:
      "While we currently do not offer gift cards, you can still gift a coaching session or subscription to your loved ones by completing the purchase on their behalf using your payment details. To gift someone a coaching service, simply get in touch with our support team at support@jobtest.org. Once the payment is made, the person you are gifting the coaching service to will need to take the test through our website. Once the test has been taken, please share their email with us, so we can manually upgrade their report.",
    visible: true
  },
  {
    question: "How do I manage my subscription billing?",
    answer: `To manage your subscription billing or payments, please go to the "Your Coaching Membership" section on your Accounts page. You can find the Accounts page by clicking on "Account" on the right-positioned navigation menu.`,
    visible: userStore.isCoachingSubscriber
  },
  {
    question: "How do I change to a different subscription tier?",
    answer:
      "To change to a different subscription tier, Please reach out to our support team at support@jobtest.org and they will help with the transition.",
    visible: userStore.isCoachingSubscriber
  },
  {
    question: "Do you offer coaching services outside of the US?",
    answer:
      "While our team does not currently have coaches specifically experienced in the worldwide professional market, we remain committed to supporting your career goals. Our coaches are skilled in providing general career guidance, helping to enhance resumes, cover letters, and LinkedIn profiles, and offering strategies for career advancement. Additionally, we provide expert career coaching that can be tailored to your specific needs.",
    visible: true
  },
  {
    question: "Why was I charged again this month?",
    answer:
      "Your subscription renews automatically each month on the same date as the initial payment of your subscription, hence the recurring charge.",
    visible: userStore.isCoachingSubscriber
  },
  {
    question: "Can I skip my subscription renewal if I’m unable to attend sessions for an extended period?",
    answer:
      "We now offer the option to skip an upcoming renewal. This means your plan will not renew for the next billing cycle, and billing will automatically resume on the following scheduled renewal date. You can find the option to do so under the 'Coaching Membership' section of your 'Account' page. If you are facing a longer-term break or special situation, please reach out to our team at support@jobtest.org to discuss your options. We are happy to help.",
    visible: userStore.isCoachingSubscriber
  },
  {
    question: "Can I roll over sessions from months I missed?",
    answer:
      "While coaching sessions do not roll over month to month, your subscription provides continuous access to valuable resources and support throughout your career journey.  Be sure to take advantage of weekly access to our JobTest Career Accelerator sessions, it helps keep you motivated and connected between sessions.",
    visible: userStore.isCoachingSubscriber
  }
]

//COMPUTED
const descriptionMaxlength = computed(() =>
  isXlOrLarger.value ? 115 : isDesktopOrLarger.value ? 80 : isMobileOrLarger.value ? 100 : 70
)

// FUNCTION
const mailSupport = () => window.open("mailto:support@jobtest.org")
</script>

<style lang="scss" scoped>
.i-frequently-asked-questions {
  .set {
    margin-bottom: 15px;

    .answer {
      margin-top: 8px;
      color: $comet600;
    }
  }

  .card {
    @include flex(column, start, center, $gap: 18px);
    @include border-radius-md;

    text-align: center;
    background-color: $comet50;
    margin-top: 40px;
    padding: 24px 20px;
  }
}
</style>
